import { useState, useEffect, useRef } from 'react';
import type { FutebolUltimoEvento, FutebolJogoDia } from '@/shared/types';
import { soundEngine } from '@/shared/lib/soundEngine';
import type { FutebolSoundId } from '@/shared/lib/soundEngine';

interface Props {
  evento:     FutebolUltimoEvento | null;
  jogo:       FutebolJogoDia | null;
  initialKey?: string | null;
}

type NivelEvento = 'critico' | 'importante' | 'informativo';

const NIVEL: Record<string, NivelEvento> = {
  cartao_vermelho: 'critico',
  penalti:         'critico',
  cartao_amarelo:  'importante',
  substituicao:    'importante',
  var_decisao:     'importante',
  lesao:           'importante',
  escanteio:       'informativo',
  acrescimos:      'informativo',
};

const CONFIG: Record<NivelEvento, { duracao: number; fadeOut: number; padding: string; borderWidth: string }> = {
  critico:      { duracao: 9_000, fadeOut: 600, padding: '0.9vw 1.8vw', borderWidth: '3px' },
  importante:   { duracao: 7_000, fadeOut: 500, padding: '0.6vw 1.2vw', borderWidth: '3px' },
  informativo:  { duracao: 5_000, fadeOut: 400, padding: '0.45vw 1vw',  borderWidth: '2px' },
};

const COR: Record<string, string> = {
  cartao_amarelo:  '#facc15',
  cartao_vermelho: '#ef4444',
  substituicao:    '#34d399',
  var_decisao:     '#a78bfa',
  acrescimos:      '#94a3b8',
  penalti:         '#f97316',
  escanteio:       '#3b82f6',
  lesao:           '#f59e0b',
};

const LABEL: Record<string, string> = {
  cartao_amarelo:  'Cartão Amarelo',
  cartao_vermelho: 'Cartão Vermelho',
  substituicao:    'Substituição',
  var_decisao:     'VAR',
  acrescimos:      'Acréscimos',
  penalti:         'Pênalti!',
  escanteio:       'Escanteio',
  lesao:           'Lesão',
};

function IconEvento({ tipo, nivel }: { tipo: string; nivel: NivelEvento }) {
  const sz = nivel === 'critico' ? '2vw' : nivel === 'informativo' ? '1.3vw' : '1.6vw';
  const szMin = nivel === 'critico' ? 22 : nivel === 'informativo' ? 14 : 18;

  if (tipo === 'cartao_amarelo' || tipo === 'cartao_vermelho') {
    return (
      <div style={{
        width: nivel === 'critico' ? '1.8vw' : '1.4vw', 
        height: nivel === 'critico' ? '2.5vw' : '1.9vw',
        minWidth: nivel === 'critico' ? 20 : 16, 
        minHeight: nivel === 'critico' ? 28 : 22,
        borderRadius: 3,
        background: tipo === 'cartao_amarelo' ? '#facc15' : '#ef4444',
        boxShadow:  tipo === 'cartao_amarelo' ? '0 0 10px #facc1560' : '0 0 10px #ef444460',
      }} />
    );
  }
  if (tipo === 'substituicao') {
    return (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"
        style={{ width: sz, height: sz, minWidth: szMin, minHeight: szMin }}>
        <path d="M7 16V4m0 0L3 8m4-4l4 4" />
        <path d="M17 8v12m0 0l4-4m-4 4l-4-4" />
      </svg>
    );
  }
  if (tipo === 'var_decisao') {
    return (
      <svg viewBox="0 0 24 24" fill="currentColor"
        style={{ width: sz, height: sz, minWidth: szMin, minHeight: szMin }}>
        <path d="M12 2a10 10 0 1 0 0 20A10 10 0 0 0 12 2zm0 18a8 8 0 1 1 0-16 8 8 0 0 1 0 16zm-1-5h2v2h-2v-2zm0-8h2v6h-2V7z" />
      </svg>
    );
  }
  if (tipo === 'penalti') {
    return (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"
        style={{ width: sz, height: sz, minWidth: szMin, minHeight: szMin }}>
        <circle cx="12" cy="12" r="9" />
        <circle cx="12" cy="12" r="2.5" fill="currentColor" stroke="none" />
      </svg>
    );
  }
  if (tipo === 'escanteio') {
    return (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"
        style={{ width: sz, height: sz, minWidth: szMin, minHeight: szMin }}>
        <path d="M5 20V4" />
        <path d="M5 4l10 5-10 5" fill="currentColor" stroke="none" />
      </svg>
    );
  }
  if (tipo === 'lesao') {
    return (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
        style={{ width: sz, height: sz, minWidth: szMin, minHeight: szMin }}>
        <path d="M12 5v14M5 12h14" />
      </svg>
    );
  }
  // acrescimos
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"
      style={{ width: sz, height: sz, minWidth: szMin, minHeight: szMin }}>
      <circle cx="12" cy="12" r="10" />
      <path d="M12 8v4l3 3" />
    </svg>
  );
}

function detalheEvento(ev: FutebolUltimoEvento): string | null {
  if (ev.tipo === 'acrescimos') return `+${ev.minutos_extra ?? '?'} min`;
  if (ev.tipo === 'var_decisao') return ev.var_resultado || null;
  if (ev.tipo === 'substituicao' && ev.jogador_saiu) return `${ev.jogador} ↑  ${ev.jogador_saiu} ↓`;
  if (ev.tipo === 'substituicao') return ev.jogador;
  return ev.jogador || null;
}

/**
 * Pop-up discreto no topo-centro da TV para eventos do jogo principal
 * que não são gol (cartão, substituição, VAR, acréscimos).
 * Gol tem tratamento próprio no FutebolGolOverlay.
 */
export default function FutebolEventoPopup({ evento, jogo, initialKey }: Props) {
  const [visivel, setVisivel] = useState(false);
  const [fadeOut, setFadeOut] = useState(false);
  const prevKeyRef = useRef<string | null>(initialKey ?? null);

  const isNaoGol  = evento && evento.tipo !== 'gol';
  // Chave sem jogador: quando o WS bridge seta '–' e o REST atualiza com nome real,
  // a chave permanece igual → popup não dispara uma segunda vez (mesmo padrão do GolOverlay).
  const eventoKey = isNaoGol
    ? `${evento!.tipo}-${evento!.minuto}-${evento!.time ?? ''}`
    : null;

  useEffect(() => {
    if (!eventoKey || eventoKey === prevKeyRef.current) return;
    prevKeyRef.current = eventoKey;
    soundEngine.playSound(evento!.tipo as FutebolSoundId);

    const nivel  = NIVEL[evento!.tipo] ?? 'informativo';
    const cfg    = CONFIG[nivel];

    setFadeOut(false);
    setVisivel(true);

    const fadeTimer = setTimeout(() => setFadeOut(true),  cfg.duracao - cfg.fadeOut);
    const hideTimer = setTimeout(() => setVisivel(false), cfg.duracao);

    return () => { clearTimeout(fadeTimer); clearTimeout(hideTimer); };
  }, [eventoKey]);

  if (!visivel || !evento || !jogo) return null;

  const cor      = COR[evento.tipo]    ?? '#94a3b8';
  const titulo   = LABEL[evento.tipo]  ?? evento.tipo;
  const detalhe  = detalheEvento(evento);
  const nomeTime = evento.tipo !== 'acrescimos'
    ? (evento.time === 'casa' ? jogo.time_casa : jogo.time_visitante)
    : null;
  const minuto   = evento.tipo !== 'acrescimos' && evento.minuto > 0
    ? `${evento.minuto}'`
    : null;

  const nivel    = NIVEL[evento.tipo] ?? 'informativo';
  const cfg      = CONFIG[nivel];
  const isCritico     = nivel === 'critico';
  const isInformativo = nivel === 'informativo';

  const innerContent = (
    <div
      className={isCritico ? 'animate-evento-critico-in' : 'animate-evento-popup-in'}
      style={{
        display:        'flex',
        alignItems:     'center',
        gap:            isCritico ? '1vw' : '0.8vw',
        background:     isCritico
          ? `linear-gradient(135deg, rgba(20,5,5,0.97) 0%, rgba(30,8,8,0.95) 100%)`
          : isInformativo
            ? 'linear-gradient(135deg, rgba(10,10,14,0.88) 0%, rgba(18,18,24,0.85) 100%)'
            : 'linear-gradient(135deg, rgba(10,10,14,0.95) 0%, rgba(18,18,24,0.92) 100%)',
        backdropFilter: 'blur(20px)',
        border:         `1px solid ${cor}${isCritico ? '50' : '30'}`,
        borderLeft:     `${cfg.borderWidth} solid ${cor}`,
        borderRadius:   isCritico ? '1vw' : '0.8vw',
        padding:        cfg.padding,
        boxShadow:      isCritico
          ? `0 6px 40px rgba(0,0,0,0.65), 0 0 0 1px ${cor}25, 0 0 30px ${cor}20`
          : `0 4px 24px rgba(0,0,0,0.5), 0 0 0 1px ${cor}15`,
        opacity:        fadeOut ? 0 : isInformativo ? 0.85 : 1,
        transition:     fadeOut ? `opacity ${cfg.fadeOut}ms ease` : undefined,
        whiteSpace:     'nowrap',
      }}
    >
      {/* Ícone colorido */}
      <span style={{ color: cor, display: 'flex', alignItems: 'center' }}>
        <IconEvento tipo={evento.tipo} nivel={nivel} />
      </span>

      {/* Título */}
      <span style={{
        fontSize:    isCritico ? '1.3vw' : isInformativo ? '0.8vw' : '1vw',
        fontWeight:  900,
        color:       cor,
        letterSpacing: '0.05em',
        textTransform: 'uppercase',
      }}>
        {titulo}
      </span>

      {/* Separador */}
      {(detalhe || nomeTime) && (
        <span style={{ color: 'rgba(255,255,255,0.2)', fontSize: '1vw' }}>·</span>
      )}

      {/* Detalhe (jogador / acréscimos / VAR resultado) */}
      {detalhe && (
        <span style={{ fontSize: '0.9vw', color: '#e2e8f0', fontWeight: 600 }}>
          {detalhe}
        </span>
      )}

      {/* Time */}
      {nomeTime && (
        <span style={{ fontSize: isInformativo ? '0.7vw' : '0.8vw', color: 'rgba(255,255,255,0.4)' }}>
          {nomeTime}
        </span>
      )}

      {/* Badge Minuto */}
      {!isInformativo && minuto && (
        <span style={{
          background:   `${cor}22`,
          border:       `1px solid ${cor}40`,
          borderRadius: '0.4vw',
          padding:      '0.1vw 0.4vw',
          fontSize:     '0.75vw',
          fontWeight:   700,
          color:        cor,
          flexShrink:   0,
        }}>
          {minuto}
        </span>
      )}
    </div>
  );

  return (
    <div
      className="fixed z-40 pointer-events-none select-none"
      style={{ top: '8vw', left: '50%', transform: 'translateX(-50%)' }}
    >
      {isCritico ? (
        <div className="animate-evento-critico-shake">
          {innerContent}
        </div>
      ) : innerContent}
    </div>
  );
}
